import React from 'react';
import {
  Table,
  VertButton,
  Typography,
  Button,
  VertPagination,
  VertContainer,
  VertSelect,
} from '@vertem-core/vertice-ds';
import PropTypes from 'prop-types';
import {
  DownloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { changeCurrentPage, changePageSize } from 'redux/Reducers/ReportRedemption/ReportRedemptionSlice';
import {
  FILTER_TYPES, OPTIONS_ORDERS_STATUS, OPTIONS_REDEMPTIONS, OPTIONS_STATUS, itemsPerPage,
} from '../Constants';
import { useNavigate } from 'react-router';

const ReportTable = ({
  filterType,
  submitFunction,
  editRedeem,
}) => {
  const {
    tableInfo: reportRedemptionTableInfo,
    isLoadingTable,
    pageSize,
    currentPage,
  } = useSelector((state) => state.ReportRedemption);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAllClientsRedeems = !!filterType && filterType === FILTER_TYPES.SEE_ALL_REDEEMS.value;
  const isClientsSolicitations = !!filterType
    && filterType === FILTER_TYPES.SEE_CLIENTS_SOLICITATIONS.value;
  const mediaQuery = useMediaQuery({ query: '(max-width: 768px)' });

  const columns = [
    isAllClientsRedeems ? {
      title: 'Data',
      dataIndex: 'date',
      key: 'redeem-date',
      render: (_, { date }) => (
        <Typography.Text>{dayjs(date).format('DD/MM/YYYY')}</Typography.Text>
      ),
      width: '8%',
    } : {
      title: 'ID',
      dataIndex: 'id',
      key: 'solicitation-id',
      render: (_, { id }) => (
        <Typography.Text>{`#${id}`}</Typography.Text>
      ),
      width: '6%',
    },
    isAllClientsRedeems ? {
      title: 'Ped. Pai',
      dataIndex: 'parentRedemption',
      key: 'parentRedemption',
      render: (_, { parentOrderId }) => (
        <Typography.Text>{parentOrderId}</Typography.Text>
      ),
      width: '10%',
    } : {
      title: 'Dt. Atualização',
      dataIndex: 'solicitation-update-date',
      key: 'updateDate',
      render: (_, { updateDate }) => (
        <Typography.Text>{dayjs(updateDate).format('DD/MM/YYYY')}</Typography.Text>
      ),
      width: '8%',
    },
    isAllClientsRedeems ? {
      title: 'Ped. Filho',
      dataIndex: 'sonRedemption',
      key: 'sonRedemption',
      render: (_, { orderId }) => (
        <Typography.Text>{orderId}</Typography.Text>
      ),
      width: '10%',
    } : {
      title: 'Valor',
      dataIndex: 'value',
      key: 'solicitation-value',
      render: (_, { value }) => (
        <Typography.Text
          style={{
            color: '#8C8C8C',
          }}
        >
          {new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(value)}
        </Typography.Text>
      ),
      width: '9%',
    },
    isAllClientsRedeems ? {
      title: 'Valor Total',
      dataIndex: 'totalValue',
      key: 'total-value',
      render: (_, { totalValue }) => (
        <div className="vert-product-table-spot-value">
          <Typography.Text
            style={{
              color: '#8C8C8C',
            }}
          >
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(totalValue).replace('R$', '')}
          </Typography.Text>
        </div>
      ),
      width: '15%',
    } : {
      title: 'Solicitante',
      dataIndex: 'solicitationResponsible',
      key: 'solicitation-responsible',
      render: (_, { managerName }) => (
        <Typography.Text>{managerName}</Typography.Text>
      ),
      width: '10%',
    },
    {
      title: 'Participante',
      dataIndex: 'participant',
      key: 'participant',
      render: (_, { participant }) => (
        <Typography.Text>{participant}</Typography.Text>
      ),
      width: '15%',
    },
    isAllClientsRedeems ? {
      title: 'Fornecedor',
      dataIndex: 'supplier',
      key: 'supplier',
      render: (_, { supplier }) => (
        <Typography.Text>{supplier}</Typography.Text>
      ),
      width: '15%',
    } : {
      title: 'Tipo',
      dataIndex: 'solicitationType',
      key: 'solicitation-type',
      render: (_, { solicitationType }) => (
        <Typography.Text>
          {Object.values(OPTIONS_REDEMPTIONS)
            .find((status) => status.value === solicitationType).label}
        </Typography.Text>
      ),
      width: '10%',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, { status }) => (
        <Typography.Text style={{
          color: `${Object.values(isClientsSolicitations ? OPTIONS_STATUS : OPTIONS_ORDERS_STATUS)
            .find((option) => option.value === status)?.color}`,
        }}
        >
          {
            Object.values(isClientsSolicitations ? OPTIONS_STATUS : OPTIONS_ORDERS_STATUS)
              .find((option) => option.value === status)?.label
          }
        </Typography.Text>
      ),
      width: '10%',
    },
    {
      title: mediaQuery ? '' : 'Ações',
      dataIndex: 'actions',
      key: 'actions',
      width: '10%',
      render: (_, payload) => (
        <div className="vert-product-table-spot">
          <VertButton
            type="highlight"
            shape="round"
            icon={<SearchOutlined />}
            size="small"
            onClick={() => {
              isClientsSolicitations
                ? navigate(`/approve-redemption/${payload.id}`)
                : editRedeem(payload)
            }}
          />
          {isClientsSolicitations &&
          payload.term && (
            <VertButton
              type="default"
              shape="round"
              icon={<DownloadOutlined />}
              size="small"
              download
              href={payload.term}
            />
          )}
        </div>
      ),
    },
  ];

  const tableResults = () => {
    const solicitationsInfo = () => {
      return (
        reportRedemptionTableInfo
        && reportRedemptionTableInfo.data
        && reportRedemptionTableInfo.data.length
        && reportRedemptionTableInfo.data.map((item) => (
          {
            key: item.id,
            updateDate: item.conclusionDate,
            value: item.totalValue,
            managerName: item.managerName,
            participant: item.participantName,
            solicitationType: item.type,
            status: item.redemptionStatus,
            id: item.id,
            term: item.term
          }
        ))
      ) || [];
    };

    const redeemsInfo = () => {
      return (
        reportRedemptionTableInfo
        && reportRedemptionTableInfo.data
        && reportRedemptionTableInfo.data.length
        && reportRedemptionTableInfo.data.map((item) => (
          {
            key: item.orderId,
            date: item.orderDate,
            parentOrderId: item.parentOrderId,
            orderId: item.orderId,
            totalValue: item.totalValue,
            participant: item.participantName,
            supplier: item.supplierName,
            status: item.statusId,
            id: item.orderId,
          }
        ))
      ) || [];
    };

    if (isClientsSolicitations) {
      return solicitationsInfo();
    }

    if (isAllClientsRedeems) {
      return redeemsInfo();
    }

    return [];
  };

  return (
    <>
      <Table
        locale={{
          emptyText: 'Nenhum resultado encontrado',
        }}
        loading={isLoadingTable}
        columns={columns}
        dataSource={tableResults()}
        pagination={false}
        bordered
        tableLayout="auto"
        sticky
        size="small"
        scroll={{ x: 1000 }}
      />
      {!!reportRedemptionTableInfo?.data?.length && (
        <VertContainer className="margin-top-small">
          <div
            className="col-12"
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <Typography.Text className="margin-right-small">
              Itens por página:
            </Typography.Text>
            <VertSelect
              defaultValue={15}
              className="col-1"
              options={
                [
                  ...itemsPerPage,
                  {
                    value: reportRedemptionTableInfo?.totalCount,
                    label: 'Todos',
                  },
                ]
              }
              onChange={(e) => {
                dispatch(changePageSize(e));
                submitFunction(e);
              }}
            />
            <VertPagination
              current={currentPage}
              total={reportRedemptionTableInfo?.totalCount}
              defaultCurrent={1}
              defaultPageSize={15}
              pageSize={pageSize}
              onChange={(e) => dispatch(changeCurrentPage(e))}
            />
          </div>
        </VertContainer>

      )}
    </>
  );
};

export default ReportTable;

ReportTable.PropTypes = {
  filterType: PropTypes.number.isRequired,
  submitFunction: PropTypes.func.isRequired,
};
